import useRefCode from "@casino/legacy/client/hooks/cookies/marketing/useRefCode";
import useQueryParam from "@casino/legacy/client/hooks/router/useQueryParam";
import useRouter from "@casino/legacy/client/hooks/router/useRouter";
import useLanguage from "@casino/legacy/client/hooks/useLanguage";
import useSite from "@casino/legacy/client/hooks/useSite";
import FRIENDREF_COOKIE from "@workspace/casino_config/cookies/FRIENDREF_COOKIE";
import REF_AFF from "@workspace/casino_config/cookies/REF_AFF";
import { useMetamaskAuthMutation } from "@workspace/casino_core/mutations/MetamaskAuthMutation.client";
import { getCookie, setCookie } from "@workspace/cookies/browser";
import { useMetaMask } from "metamask-react";
import type { FieldValues, UseFormSetError } from "react-hook-form";
import getAccessCode from "../../modules/helpers/getAccessCode";
import useHandleMetamaskAuthResponse from "./helpers/handleMetamaskAuthResponse";

type Args<T extends FieldValues> = {
  values: Omit<MetamaskDepositFormValues, "dateOfBirth"> & {
    dateOfBirth?: number;
    accessCode?: string;
    ethAddress?: string;
  };
  setError?: UseFormSetError<T>;
  setErrors?: (errorsFragment: GenericErrorType[]) => void;
  setOtpError?: () => void;
  otp?: string;
  redirectUrl: string;
};

const useAuthMetamaskUser = () => {
  const refCode = useRefCode();
  const refAff = getCookie(REF_AFF);
  const friendRef = getCookie(FRIENDREF_COOKIE);
  const cid = getCookie("cid");
  const [ethAddr] = useQueryParam("addr");
  const { ethereum } = useMetaMask();
  const { asPath } = useRouter();
  const { site } = useSite();
  const { code: language } = useLanguage();
  const [metamaskAuth, { loading }] = useMetamaskAuthMutation();

  const { handleMetamaskAuthResponse } = useHandleMetamaskAuthResponse();

  const handleMetamaskAuth = async <T extends FieldValues>({
    values,
    setError,
    setErrors,
    setOtpError,
    otp,
    redirectUrl,
  }: Args<T>) => {
    setCookie("social_auth_type", "metamask");

    const { ethAddress: ethAccountAddress } = values;
    const address = ethAddr || ethAccountAddress || "";

    const accessCode = await getAccessCode({ address, site });

    const signature = await ethereum.request({
      method: "personal_sign",
      params: [`${site}: ${accessCode}`, address],
    });

    const optionalVariables = asPath.includes("/complete-registration")
      ? {
          username: values.username,
          password: values.password,
          email: values.email,
          phone: values.phone,
          offers: values.offers,
          dateOfBirth: values.dateOfBirth,
          language,
          refAff,
          refCode,
          friendRef,
          cid,
        }
      : {};

    const { data } = await metamaskAuth({
      input: {
        ...optionalVariables,
        address,
        signature: signature as string,
        site,
        otp,
      },
    });

    return handleMetamaskAuthResponse<T>({
      setError,
      setErrors,
      setOtpError,
      redirectUrl,
      data: data?.userManagementMetamaskAuthenticate ?? {},
      address,
    });
  };

  return { loading, handleMetamaskAuth };
};

export default useAuthMetamaskUser;
